'use client'

import { useCallback, useEffect, useState } from 'react'

// Margin report card above the Rate Cards list — the natural home: the
// numbers here are what the cards' pricing produces. Retail charged vs
// wholesale estimate vs gross margin, per client, for a selectable period.
// Tabular numerics, high signal-to-ink.

const usd = (cents) => `$${(cents / 100).toFixed(2)}`

function defaultPeriod() {
  const now = new Date()
  const prev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))
  return `${prev.getUTCFullYear()}-${String(prev.getUTCMonth() + 1).padStart(2, '0')}`
}

export function MarginReportPanel() {
  const [period, setPeriod] = useState(defaultPeriod())
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  const load = useCallback(async (p) => {
    setError('')
    const res = await fetch(`/api/billing/margin-report?period=${encodeURIComponent(p)}`, {
      credentials: 'include',
    })
    if (res.ok) setData(await res.json())
    else setError('Failed to load margin report')
  }, [])

  useEffect(() => {
    load(period)
  }, [load, period])

  const cell = { padding: '6px 12px', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }
  const cellLeft = { ...cell, textAlign: 'left' }

  return (
    <div
      style={{
        border: '1px solid var(--theme-elevation-150)',
        borderRadius: 8,
        padding: '14px 16px',
        background: 'var(--theme-elevation-50)',
        marginBottom: 24,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Margin report</h3>
        <input
          type="month"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          style={{
            padding: '4px 8px',
            borderRadius: 6,
            border: '1px solid var(--theme-elevation-250)',
            fontSize: 13,
            background: 'var(--theme-elevation-0)',
            color: 'inherit',
          }}
        />
      </div>
      {error && <p style={{ color: 'var(--theme-error-500, #b00020)', fontSize: 13 }}>{error}</p>}
      {data && data.rows.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--theme-elevation-500)', margin: '10px 0 0' }}>
          No charges in {data.period}.
        </p>
      )}
      {data && data.rows.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 13, minWidth: 520 }}>
            <thead>
              <tr style={{ color: 'var(--theme-elevation-500)', textAlign: 'right' }}>
                <th style={cellLeft}>Client</th>
                <th style={cell}>Retail</th>
                <th style={cell}>Wholesale est.</th>
                <th style={cell}>Margin</th>
                <th style={cell}>%</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.clientId} style={{ borderTop: '1px solid var(--theme-elevation-100)' }}>
                  <td style={cellLeft}>
                    {r.clientName}
                    {r.isDemo ? ' (demo, zero-rated)' : ''}
                  </td>
                  <td style={cell}>{usd(r.retailCents)}</td>
                  <td style={cell}>{usd(r.wholesaleCents)}</td>
                  <td style={cell}>{usd(r.marginCents)}</td>
                  <td style={cell}>{r.marginPct === null ? '—' : `${r.marginPct}%`}</td>
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid var(--theme-elevation-250)', fontWeight: 600 }}>
                <td style={cellLeft}>Total (excl. demos)</td>
                <td style={cell}>{usd(data.totals.retailCents)}</td>
                <td style={cell}>{usd(data.totals.wholesaleCents)}</td>
                <td style={cell}>{usd(data.totals.marginCents)}</td>
                <td style={cell}>
                  {data.totals.marginPct === null ? '—' : `${data.totals.marginPct}%`}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
